import { supabase } from "@/integrations/supabase/client";
import {
  activityUiPrefsDisableAutoSchedule,
  parseActivitySettings,
  type OrganizationActivitySettings,
} from "@/lib/activitySettingsSchema";
import { requestActivityScheduleDraft } from "@/lib/activityScheduleBridge";
import { getDealPipelineId, hasFutureOpenActivitiesForContext } from "@/lib/activitySchedulingGuards";
import { dateFromFollowUpPreset } from "@/lib/followUpPresetDates";

interface ActivityRow {
  id: string;
  org_id: string;
  deal_id?: string | null;
  lead_id?: string | null;
  company_id?: string | null;
  owner_id?: string | null;
}

export async function maybeOpenScheduleAfterActivityMarkedDone(params: {
  orgId: string;
  previousStatus: string | null | undefined;
  activity: ActivityRow;
}): Promise<void> {
  const { orgId, previousStatus, activity } = params;
  if (previousStatus === "done") return;
  if (!activity?.id) return;

  const { data: orgRow, error: orgErr } = await supabase
    .from("organizations")
    .select("activity_settings")
    .eq("id", orgId)
    .single();
  if (orgErr) {
    console.warn("[maybeOpenScheduleAfterActivityMarkedDone] org", orgErr);
    return;
  }

  const settings = parseActivitySettings(orgRow?.activity_settings) as OrganizationActivitySettings;
  const cfg = settings.afterCompleteActivity;
  if (!cfg?.enabled) return;

  if (cfg.scope === "pipelines") {
    if (!activity.deal_id) return;
    const pipelineId = await getDealPipelineId(activity.deal_id);
    const ids = cfg.pipelineIds || [];
    if (ids.length === 0) return;
    if (!pipelineId || !ids.includes(pipelineId)) return;
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return;

  const { data: mem } = await supabase
    .from("memberships")
    .select("role")
    .eq("user_id", user.id)
    .eq("org_id", orgId)
    .maybeSingle();
  const role = (mem as { role?: string } | null)?.role;

  const { data: prof } = await supabase
    .from("profiles")
    .select("activity_ui_prefs")
    .eq("user_id", user.id)
    .maybeSingle();

  const prefsOff = activityUiPrefsDisableAutoSchedule(
    (prof as { activity_ui_prefs?: unknown } | null)?.activity_ui_prefs as never,
    orgId
  );
  if (prefsOff && role !== "admin" && role !== "owner") {
    return;
  }

  const busy = await hasFutureOpenActivitiesForContext({
    orgId,
    dealId: activity.deal_id,
    leadId: activity.lead_id,
    companyId: activity.company_id,
    excludeActivityId: activity.id,
  });
  if (busy) return;

  requestActivityScheduleDraft({
    dealId: activity.deal_id,
    leadId: activity.lead_id,
    companyId: activity.company_id,
    ownerId: activity.owner_id,
    activityTypeKey: "task",
    scheduledFor: null,
  });
}

export async function maybeOpenScheduleAfterDealWon(params: {
  orgId: string;
  dealId: string;
  leadId?: string | null;
  companyId?: string | null;
  ownerId?: string | null;
}): Promise<void> {
  const { orgId, dealId, leadId, companyId, ownerId } = params;

  const { data: orgRow, error: orgErr } = await supabase
    .from("organizations")
    .select("activity_settings")
    .eq("id", orgId)
    .single();
  if (orgErr) {
    console.warn("[maybeOpenScheduleAfterDealWon] org", orgErr);
    return;
  }

  const settings = parseActivitySettings(orgRow?.activity_settings) as OrganizationActivitySettings;
  const cfg = settings.afterDealWon;
  if (!cfg?.enabled) return;

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return;

  const { data: mem } = await supabase
    .from("memberships")
    .select("role")
    .eq("user_id", user.id)
    .eq("org_id", orgId)
    .maybeSingle();
  const role = (mem as { role?: string } | null)?.role;

  const { data: prof } = await supabase
    .from("profiles")
    .select("activity_ui_prefs")
    .eq("user_id", user.id)
    .maybeSingle();

  const prefsOff = activityUiPrefsDisableAutoSchedule(
    (prof as { activity_ui_prefs?: unknown } | null)?.activity_ui_prefs as never,
    orgId
  );
  if (cfg.allowMembersToOptOut && prefsOff && role !== "admin" && role !== "owner") {
    return;
  }

  const busy = await hasFutureOpenActivitiesForContext({
    orgId,
    dealId,
    leadId,
    companyId,
  });
  if (busy) return;

  const when = dateFromFollowUpPreset(cfg.followUpPreset || "3m", new Date());

  requestActivityScheduleDraft({
    dealId,
    leadId,
    companyId,
    ownerId,
    activityTypeKey: cfg.defaultActivityTypeKey || "task",
    scheduledFor: when,
  });
}
